import { useEffect, useState, type CSSProperties } from 'react';

const BANNER_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  padding: '8px 16px',
  background: '#b45309',
  color: '#fff',
  textAlign: 'center',
  fontSize: 14,
  fontWeight: 500,
};

const TOAST_STYLE: CSSProperties = {
  position: 'fixed',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 9999,
  padding: '10px 16px',
  background: '#065f46',
  color: '#fff',
  borderRadius: 8,
  fontSize: 14,
  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
};

const RECONNECT_TOAST_MS = 3000;

/**
 * Global offline indicator (audit Fix #43).
 *
 * - Shows a fixed-top pt-BR banner while `navigator.onLine === false` (or any
 *   `offline` window event).
 * - On reconnect after being offline, shows a brief auto-dismissing toast.
 * - Mounted in shell App.tsx so it covers BOTH shell pages and every loaded
 *   app bundle (the bundle's React tree is rendered as descendants of App).
 */
export function OfflineBanner() {
  const [offline, setOffline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );
  const [showReconnectToast, setShowReconnectToast] = useState(false);

  useEffect(() => {
    let toastTimer: ReturnType<typeof setTimeout> | undefined;
    let lastWasOffline = !navigator.onLine;

    function handleOffline() {
      lastWasOffline = true;
      setOffline(true);
      setShowReconnectToast(false);
      if (toastTimer) {
        clearTimeout(toastTimer);
        toastTimer = undefined;
      }
    }
    function handleOnline() {
      const wasOffline = lastWasOffline;
      lastWasOffline = false;
      setOffline(false);
      if (wasOffline) {
        setShowReconnectToast(true);
        toastTimer = setTimeout(() => setShowReconnectToast(false), RECONNECT_TOAST_MS);
      }
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      if (toastTimer) clearTimeout(toastTimer);
    };
  }, []);

  return (
    <>
      {offline ? (
        <div role="status" aria-live="polite" style={BANNER_STYLE}>
          Você está sem conexão. Algumas ações podem falhar até a internet voltar.
        </div>
      ) : null}
      {showReconnectToast ? (
        <div role="status" aria-live="polite" style={TOAST_STYLE}>
          Você voltou. Sincronizando…
        </div>
      ) : null}
    </>
  );
}
